// ============================================================================
// Steadfastly - Nine Questions Scoring Utilities
// ============================================================================
import { NINE_QUESTIONS } from '../constants/nineQuestions';

// Placeholder phrases that block attestation (spec section 4.1)
export const PLACEHOLDER_PHRASES = [
  "per policy",
  "management decision",
  "industry standard",
  "as discussed",
  "tbd",
  "to be determined",
  "see above",
  "standard practice",
  "n/a",
  "none",
  "test",
  "asdf",
  "xxx",
  "placeholder",
  "todo",
  "fixme",
  "pending",
];

// Extended placeholder rationales detection
export const PLACEHOLDER_RATIONALES = [
  'per policy', 'per policies', 'per the policy',
  'industry standard', 'industry standards', 'industry-standard',
  'best practice', 'best practices', 'best-practice',
  'common practice', 'common practices', 'standard practice', 'standard practices',
  'management decision', 'management decisions', 'management approved',
  'based on policy', 'in accordance with policy', 'consistent with policy',
  'as discussed', 'as agreed', 'as decided', 'as determined', 'as directed',
  'previously discussed', 'per discussion', 'per our discussion',
  'it was decided', 'we decided', 'the committee decided',
  'tbd', 'to be determined', 'to be completed', 'to be added',
  'pending', 'pending review', 'pending approval',
  'n/a', 'not applicable', 'none', 'null',
  'see above', 'see below', 'see previous', 'see attached',
  'same as before', 'no change', 'unchanged',
  'as required', 'as required by regulation', 'per regulation', 'per regulations',
  'to comply with regulations', 'for compliance', 'for regulatory compliance',
  'based on risk assessment', 'per risk assessment',
  'test', 'testing', 'asdf', 'xxx', 'yyy', 'zzz',
  'placeholder', 'todo', 'fixme', 'temp', 'temporary',
  'lorem ipsum', 'sample text',
  'other banks do', 'competitors use', 'peer banks', 'similar institutions',
  'everyone does it', 'thats how its done', "that's how it's done"
];

// Enhanced placeholder detection with regex for variations
export function hasPlaceholderRationale(text) {
  if (!text) return true;
  const lower = text.toLowerCase().trim();
  
  if (PLACEHOLDER_RATIONALES.some(p => lower.includes(p))) return true;
  
  const regexPatterns = [
    /\bindustry[\s-]?standards?\b/i,
    /\bper\s+(?:our|the|company|bank)\s+policy\b/i,
    /\bmanagement\s+(?:decided|approved|direction)\b/i,
    /\b(?:to|for)\s+comply\b/i,
  ];
  
  return regexPatterns.some(p => p.test(lower));
}

export function detectPlaceholders(text, requireMinLength = false) {
  if (!text || typeof text !== "string") return [];
  const lower = text.toLowerCase().trim();
  const found = PLACEHOLDER_PHRASES.filter(phrase => lower.includes(phrase));
  
  if (requireMinLength && text.trim().length > 0 && text.trim().length < 50) {
    found.push("too short (min 50 chars)");
  }
  
  return found;
}

export function hasPlaceholder(text, requireMinLength = false) {
  return detectPlaceholders(text, requireMinLength).length > 0;
}

// WHY field validation patterns
const WHY_DATA_PATTERNS = [
  /\d+[-%]\s*(of|showed|indicated|found|revealed)/i,
  /analysis\s*(of|showed|revealed|indicated)/i,
  /\d+-month\s*(review|analysis|period|data)/i,
  /data\s*(from|showed|indicated|analysis)/i,
  /review\s*(of|showed|found)/i,
  /transaction(s)?\s*(analysis|data|review)/i,
  /SAR\s*(data|filing|analysis)/i,
  /CTR\s*(data|filing|analysis)/i
];

const WHY_ALT_PATTERNS = [
  /considered\s+.*\s+but\s+(rejected|determined|decided)/i,
  /alternative(s)?\s*(was|were)?\s*(rejected|evaluated|considered)/i,
  /evaluated\s+.*\s+option/i,
  /compared\s+to/i,
  /rejected\s+(due|because|as)/i,
  /instead\s+of/i,
  /rather\s+than/i
];

const WHY_CITATION_PATTERNS = [
  /\d+\s*CFR\s*\d+/i,
  /FinCEN\s*(Advisory|Guidance|FIN-\d+)/i,
  /OCC\s*(Bulletin|guidance)/i,
  /FFIEC\s*(manual|guidance|BSA)/i,
  /Bank\s*Secrecy\s*Act/i,
  /Section\s*\d+/i,
  /\d+\s*USC\s*\d+/i
];

export function validateWhyComponents(text) {
  if (!text || typeof text !== "string") {
    return {
      isComplete: false,
      score: 0,
      components: {
        dataAnalysis: { present: false, matches: [] },
        regulatoryCitation: { present: false, matches: [] },
        alternativesConsidered: { present: false, matches: [] }
      },
      missingComponents: ["Data Analysis", "Regulatory Citation", "Alternatives Considered"]
    };
  }

  const result = {
    isComplete: false,
    score: 0,
    components: {
      dataAnalysis: { present: false, matches: [] },
      regulatoryCitation: { present: false, matches: [] },
      alternativesConsidered: { present: false, matches: [] }
    },
    missingComponents: []
  };

  for (const pattern of WHY_DATA_PATTERNS) {
    const match = text.match(pattern);
    if (match) {
      result.components.dataAnalysis.present = true;
      result.components.dataAnalysis.matches.push(match[0]);
      break;
    }
  }

  for (const pattern of WHY_CITATION_PATTERNS) {
    const match = text.match(pattern);
    if (match) {
      result.components.regulatoryCitation.present = true;
      result.components.regulatoryCitation.matches.push(match[0]);
      break;
    }
  }

  for (const pattern of WHY_ALT_PATTERNS) {
    const match = text.match(pattern);
    if (match) {
      result.components.alternativesConsidered.present = true;
      result.components.alternativesConsidered.matches.push(match[0]);
      break;
    }
  }

  if (result.components.dataAnalysis.present) result.score += 35;
  if (result.components.regulatoryCitation.present) result.score += 35;
  if (result.components.alternativesConsidered.present) result.score += 30;

  if (!result.components.dataAnalysis.present) {
    result.missingComponents.push("Data Analysis (specific internal data)");
  }
  if (!result.components.regulatoryCitation.present) {
    result.missingComponents.push("Regulatory Citation (CFR, FinCEN, etc.)");
  }
  if (!result.components.alternativesConsidered.present) {
    result.missingComponents.push("Alternatives Considered (rejected options)");
  }

  result.isComplete = result.score >= 70;
  return result;
}

export function detectWhyIssues(text) {
  const issues = [];
  const placeholders = detectPlaceholders(text, true);
  issues.push(...placeholders);
  
  if (text && text.trim().length >= 50) {
    const validation = validateWhyComponents(text);
    if (!validation.isComplete) {
      issues.push(...validation.missingComponents.map(c => `Missing: ${c}`));
    }
  }
  
  return issues;
}

// EQ Score calculation
export function calcEQScore(item) {
  let score = 0;
  if (item.owner_id || item.owner_email) score++;
  if (item.name && item.description) score++;
  if (item.created_at && item.valid_from) score++;
  if (item.regulatory_basis && item.regulatory_basis.length >= 50 && !hasPlaceholderRationale(item.regulatory_basis)) score++;
  if (item.documentation_location) score++;
  if (item.authority_citation) score++;
  if (item.evidence_summary || (item.evidence_attachments && item.evidence_attachments.length > 0)) score++;
  if (item.next_review_date) score++;
  if (item.training_requirements) score++;
  return score;
}

export function getMissingFields(item) {
  const missing = [];
  if (!item.owner_id && !item.owner_email) missing.push("WHO");
  if (!item.name || !item.description) missing.push("WHAT");
  if (!item.created_at || !item.valid_from) missing.push("WHEN");
  if (!item.regulatory_basis || item.regulatory_basis.length < 50 || hasPlaceholderRationale(item.regulatory_basis)) missing.push("WHY");
  if (!item.documentation_location) missing.push("WHERE");
  if (!item.authority_citation) missing.push("AUTHORITY");
  if (!item.evidence_summary && (!item.evidence_attachments || item.evidence_attachments.length === 0)) missing.push("EVIDENCE");
  if (!item.next_review_date) missing.push("REVIEW");
  if (!item.training_requirements) missing.push("TRAINING");
  return missing;
}

export function getNineQuestion(label) {
  return NINE_QUESTIONS.find(q => q.label === label || q.key === label.toLowerCase());
}

export function getEQStatus(score) {
  if (score >= 9) return { label: "Examiner Ready", color: "emerald", bgColor: "bg-emerald-50", borderColor: "border-emerald-200" };
  if (score >= 6) return { label: "Gaps Exist", color: "amber", bgColor: "bg-amber-50", borderColor: "border-amber-200" };
  if (score >= 4) return { label: "Significant Vulnerability", color: "orange", bgColor: "bg-orange-50", borderColor: "border-orange-200" };
  return { label: "High Risk", color: "red", bgColor: "bg-red-50", borderColor: "border-red-200" };
}

// Review SLA Calculation
export function calculateReviewSLA(update) {
  const impactLevel = update.impact_guess || "low";
  const publishedAt = update.published_at ? new Date(update.published_at) : new Date(update.created_at || Date.now());
  const now = new Date();
  const hoursElapsed = (now - publishedAt) / (1000 * 60 * 60);
  
  const SLA_THRESHOLDS = {
    critical: { yellowHours: 1, redHours: 24 },
    high: { yellowHours: 24, redHours: 168 },
    medium: { yellowHours: 72, redHours: 720 },
    low: null,
  };
  
  const threshold = SLA_THRESHOLDS[impactLevel];
  
  if (!threshold) {
    return { status: "none", label: "Informational", color: "slate", hoursRemaining: null };
  }
  
  if (hoursElapsed >= threshold.redHours) {
    const daysOverdue = Math.floor((hoursElapsed - threshold.redHours) / 24);
    return { 
      status: "overdue", 
      label: `🔴 OVERDUE ${daysOverdue > 0 ? `(${daysOverdue}d)` : ""}`, 
      color: "red",
      hoursRemaining: -(hoursElapsed - threshold.redHours),
    };
  }
  
  if (hoursElapsed >= threshold.yellowHours) {
    const hoursUntilRed = threshold.redHours - hoursElapsed;
    const timeLabel = hoursUntilRed >= 24 
      ? `${Math.floor(hoursUntilRed / 24)}d ${Math.floor(hoursUntilRed % 24)}h`
      : `${Math.floor(hoursUntilRed)}h`;
    return { 
      status: "warning", 
      label: `⚠️ ${timeLabel} until overdue`, 
      color: "amber",
      hoursRemaining: hoursUntilRed,
    };
  }
  
  const hoursUntilYellow = threshold.yellowHours - hoursElapsed;
  const timeLabel = hoursUntilYellow >= 24 
    ? `${Math.floor(hoursUntilYellow / 24)}d ${Math.floor(hoursUntilYellow % 24)}h`
    : `${Math.floor(hoursUntilYellow)}h`;
  return { 
    status: "ok", 
    label: `✓ ${timeLabel} remaining`, 
    color: "emerald",
    hoursRemaining: hoursUntilYellow,
  };
}

// Internal mapping for analyzeNineQuestions (supports 9 questions)
const NINE_QUESTIONS_INTERNAL = NINE_QUESTIONS.map(q => ({
  num: q.number,
  label: q.label,
  desc: q.question,
  fields: q.fields,
  required: q.required,
  checkPlaceholder: q.key === 'why',
}));

export function analyzeNineQuestions(item) {
  const results = NINE_QUESTIONS_INTERNAL.map(q => {
    const values = q.fields.map(f => item[f]).filter(Boolean);
    const hasValue = values.length > 0;
    const value = values.join(" / ");
    
    let placeholders = [];
    let whyValidation = null;
    
    if (q.checkPlaceholder) {
      placeholders = detectPlaceholders(value, true);
      
      if (value && value.trim().length >= 50 && placeholders.length === 0) {
        whyValidation = validateWhyComponents(value);
        if (!whyValidation.isComplete) {
          placeholders.push(...whyValidation.missingComponents.map(c => `Missing: ${c}`));
        }
      }
    }
    
    const isComplete = hasValue && placeholders.length === 0;
    
    return {
      ...q,
      value: value || null,
      hasValue,
      isComplete,
      placeholders,
      hasPlaceholder: placeholders.length > 0,
      whyValidation,
    };
  });
  
  const score = results.filter(r => r.isComplete).length;
  const status = getEQStatus(score);
  const missing = results.filter(r => !r.isComplete).map(r => r.label);
  const hasAnyPlaceholder = results.some(r => r.hasPlaceholder);
  
  const whyResult = results.find(r => r.checkPlaceholder);
  const whyScore = whyResult?.whyValidation?.score || 0;
  
  return { results, score, status, missing, hasAnyPlaceholder, whyScore };
}
